import { useEffect, useRef, useState } from 'react';
import { getAdHocUploadUrl, uploadPhoto } from '../lib/api';
import { sniffPhotoType } from '../lib/sniff';
import { SUPPORTED_PHOTO_TYPES, type SupportedPhotoType } from '../lib/types';
import { useCategories } from './useCatalog';
import { presentUploadFailure } from './errors';
import type { GenerationTarget } from './useGenerationQueue';
import { Alert } from '../ui/Alert';
import { Button } from '../ui/Button';
import { Chip } from '../ui/Chip';
import { Modal } from '../ui/Modal';
import { Spinner } from '../ui/Spinner';
import { TextField } from '../ui/Field';

/**
 * **Ad-hoc try-on**: photograph a garment at the counter and try it on the
 * customer already standing there, with no catalogue entry behind it.
 *
 * The requirement it answers: stores whose SKUs turn over faster than anyone can
 * enter them cannot keep a catalogue current, so the grid stops being the list
 * of what they can actually sell. This is the way round that — and it is
 * deliberately *beside* the catalogue rather than instead of it, because the
 * catalogue is still the better path for anything that will be in stock next
 * week.
 *
 * ⚠️ **A sheet on the try-on screen, not a route.** `/capture` owns the
 * customer's own photo and is entangled with `CustomerSession` — a visit, a
 * retake, an on-device blob, and a leave-warning that reads the path. None of
 * that applies to a garment, and the salesman must not lose the try-on screen
 * (or the customer's place in it) to photograph something. So this duplicates
 * the small parts of `CaptureScreen` it needs rather than parameterising it.
 *
 * ⚠️ **Deliberately does NOT run `cleanBackground`.** That is a *person*
 * segmentation model — it finds a human and removes everything else. Pointed at
 * a garment on a counter it either finds no person and returns nothing, or
 * finds enough of one to cut the garment apart. The original bytes go up.
 */

const ACCEPT = SUPPORTED_PHOTO_TYPES.join(',');

interface AdHocGarmentModalProps {
  open: boolean;
  onClose: () => void;
  /** Queues the generation. The caller owns the concurrency notice, as it does for a tile. */
  onGenerate: (target: GenerationTarget) => void;
}

export function AdHocGarmentModal({ open, onClose, onGenerate }: AdHocGarmentModalProps) {
  const categories = useCategories();

  const cameraInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  /**
   * ⚠️ Decided at pick time and held, never re-derived from `file.type`. It is
   * signed into the presigned url *and* sent as the PUT header, and for a file
   * the picker mislabelled it is not recoverable from the File at all — the
   * `SignatureDoesNotMatch` trap `CaptureScreen` documents at length.
   */
  const [photoType, setPhotoType] = useState<SupportedPhotoType | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [category, setCategory] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [error, setError] = useState<{ message: string; detail?: string } | null>(null);
  const [uploading, setUploading] = useState(false);

  // The object URL outlives every render that made it, so it is revoked on
  // unmount as well as on each replacement.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  function reset(): void {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setFile(null);
    setPhotoType(null);
    setCategory(null);
    setName('');
    setError(null);
  }

  function close(): void {
    reset();
    onClose();
  }

  async function onPick(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const picked = event.target.files?.[0];
    // Reset so picking the same file twice still fires a change event.
    event.target.value = '';
    if (!picked) return;

    // An empty file is its own failure — a cloud-picker entry whose backing
    // download failed arrives as a real File of zero bytes and would otherwise
    // sniff as nothing and upload as nothing.
    if (picked.size === 0) {
      setError({
        message: 'That photo could not be read. Take a new one with the camera.',
        detail: `empty file — "${picked.name || 'unnamed'}" arrived with 0 bytes`,
      });
      return;
    }

    /**
     * ⚠️ **The bytes decide, not `picked.type`.** Google Photos and Drive-backed
     * pickers routinely declare `''` or `application/octet-stream`, and refusing
     * on that told people an ordinary JPEG was an unsupported format.
     */
    const type = await sniffPhotoType(picked);

    if (!type) {
      setError({
        message: 'That file is not a supported image. Use the camera, or a JPEG, PNG or WEBP.',
        detail: `unrecognised image — declared "${picked.type || 'nothing'}", ${picked.size} bytes`,
      });
      return;
    }

    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setError(null);
    setFile(picked);
    setPhotoType(type);
    /**
     * HEIC cannot be rendered by `<img>` outside Apple platforms, and the
     * generation is fine either way — so a missing preview must never read as a
     * failed capture. Unlike `CaptureScreen` this shows no placeholder for it:
     * the salesman is looking at the garment itself, so an unrendered thumbnail
     * costs them nothing.
     */
    setPreviewUrl(
      type === 'image/heic' || type === 'image/heif' ? null : URL.createObjectURL(picked),
    );
  }

  async function confirm(): Promise<void> {
    if (!file || !photoType || !category || uploading) return;

    setUploading(true);
    setError(null);
    try {
      const { uploadUrl, garmentKey } = await getAdHocUploadUrl(photoType);

      /**
       * Straight to R2, into the **public** bucket — the one place in this app
       * that presigns a PUT there. `uploadPhoto` is named for its first caller
       * but is only a signed PUT, and every failure it names applies here.
       *
       * ⚠️ `photoType`, not `file.type` — the value just signed into `uploadUrl`.
       */
      await uploadPhoto(uploadUrl, file, photoType);

      /**
       * ⚠️ **The queue is handed the key, and holds it for every retry.** A
       * retry must never come back through here: a second upload mints a second
       * `garmentKey`, which the server's in-flight lock keys on — so re-uploading
       * to retry would take a different lock, defeat the double-tap protection,
       * and bill twice.
       */
      onGenerate({
        kind: 'adhoc',
        garmentKey,
        category,
        // The salesman's label is optional; the category is the honest fallback
        // because it is the one thing about this garment the system does know.
        name: name.trim() || category,
      });

      close();
    } catch (err) {
      const failure = presentUploadFailure(err);
      // Nothing server-side witnesses this request, so the console is the only
      // other record that it happened at all.
      console.error('[adhoc] garment upload failed:', failure.detail, err);
      setError(failure);
    } finally {
      setUploading(false);
    }
  }

  const ready = file !== null && photoType !== null && category !== null;

  return (
    <Modal
      open={open}
      onClose={close}
      title="Try something not in the catalogue"
      footer={
        <>
          <Button variant="secondary" size="md" onClick={close} disabled={uploading}>
            Cancel
          </Button>
          <Button size="md" onClick={() => void confirm()} loading={uploading} disabled={!ready}>
            Generate
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-secondary">
          Photograph the garment on its own — laid flat or on a hanger, filling the frame. It is
          tried on the customer photo you already have.
        </p>

        {previewUrl ? (
          <img
            src={previewUrl}
            alt="The garment"
            className="max-h-[32dvh] w-full rounded-md bg-subtle object-contain"
          />
        ) : file ? (
          // A picked HEIC. The file is fine and will generate; only the preview
          // is unavailable, and saying so is better than an empty box.
          <p className="rounded-md bg-subtle p-6 text-center text-sm text-tertiary">
            Photo ready — no preview available for this format.
          </p>
        ) : null}

        <div className="flex gap-3">
          <Button
            variant={file ? 'secondary' : 'primary'}
            size="md"
            onClick={() => cameraInputRef.current?.click()}
            disabled={uploading}
            fullWidth
          >
            {file ? 'Retake' : 'Photograph garment'}
          </Button>
          <Button
            variant="secondary"
            size="md"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            fullWidth
          >
            Choose a file
          </Button>
        </div>

        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium text-secondary">Garment type</p>
          {/*
            ⚠️ **The type is not a label — it selects the prompt.** Each category
            carries a hand-tuned `prompt_template` server-side, and it is the
            whole of what tells the model how this garment should be worn. A
            saree prompt on a kurta produces a confidently wrong image rather
            than an error, which is why this is required rather than defaulted.

            ℹ️ **This is the *in-stock* list**, the same one that draws the
            filter chips — categories the store has at least one garment in. A
            store wanting to try on a type it has never stocked has no chip to
            tap, and the fix is a names-only endpoint rather than anything here.
            Not hit in practice: categories are garment *types* and turn over far
            more slowly than the SKUs this feature exists for.
          */}
          {categories.isPending ? (
            <Spinner label="Loading garment types" />
          ) : categories.data?.categories.length ? (
            <div className="flex flex-wrap gap-2">
              {categories.data.categories.map((option) => (
                <Chip
                  key={option}
                  label={option}
                  selected={category === option}
                  disabled={uploading}
                  // Single-select, unlike the filter chips this borrows: exactly
                  // one prompt is used, so tapping the active one clears it
                  // rather than leaving two selected.
                  onToggle={() => setCategory((current) => (current === option ? null : option))}
                />
              ))}
            </div>
          ) : (
            <Alert tone="info">
              No garment types are set up yet — a catalogue garment has to exist first.
            </Alert>
          )}
        </div>

        <TextField
          label="Name (optional)"
          hint="Only for the label on the result. Defaults to the garment type."
          value={name}
          maxLength={200}
          disabled={uploading}
          onChange={(event) => setName(event.target.value)}
        />

        {error && (
          <Alert tone="danger">
            {error.message}
            {error.detail && (
              <span className="mt-1 block font-mono text-xs opacity-70">{error.detail}</span>
            )}
          </Alert>
        )}

        <p className="text-xs text-tertiary">
          This takes about a minute and uses one generation, the same as a catalogue garment.
        </p>
      </div>

      {/*
        Two inputs rather than one, for the reason `CaptureScreen` gives: on most
        Android builds `capture` on a single input removes the gallery option
        entirely, so the file path needs an input without it.
      */}
      <input
        ref={cameraInputRef}
        type="file"
        accept={ACCEPT}
        capture="environment"
        onChange={(event) => void onPick(event)}
        className="hidden"
      />
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT}
        onChange={(event) => void onPick(event)}
        className="hidden"
      />
    </Modal>
  );
}
